import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";

// How far each level reaches, in the words an admin would use. Shown under the
// level picker because "group" and "company" are not self-evident, and picking
// the wider one by mistake hands over a whole network silently.
const LEVEL_HINT = {
  network: "One brand — every group, company and office beneath it.",
  division:
    "A state or region: the network, narrowed to the jurisdictions it governs.",
  group: "One owner's business — every company and office under it.",
  company: "One licensed entity, plus the offices it trades from.",
  office: "One premises. An office can be shared by several companies.",
};

const ROLE_HINT = {
  owner:
    "Ownership stake. Sees the same as admin — the difference is transfers and billing.",
  admin: "Delegated authority. Same reach, no ownership.",
  viewer:
    "No authority. Today, only a NETWORK-level viewer does anything — it opens that network's head-office dashboard and nothing else of the app. A viewer at any other level opens nothing yet.",
};

// Shared with RoleGrantInvite.js, which asks the same three questions
// (role/level/scope) for someone who isn't a User yet — one set of hints
// either way, so the wording can't drift between the two forms.
export const Field = ({ label, error, hint, children }) => (
  <div className="space-y-2">
    <label className="form-label">{label}</label>
    {children}
    {hint ? <p className="text-xs text-darkgray">{hint}</p> : null}
    {error ? <p className="form-error-message">{error}</p> : null}
  </div>
);

export const toOptions = (rows = []) =>
  rows.map((row) => ({
    value: row._id,
    label: row.governs?.length
      ? `${row.name} (${row.governs.join(", ")})`
      : row.name,
  }));

export { LEVEL_HINT, ROLE_HINT };

export default function RoleGrantEdit({
  initialData = {},
  options,
  onSubmit,
  isSubmitting,
}) {
  const isExisting = Boolean(initialData._id);

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userId: initialData.userId || "",
      role: initialData.role || "owner",
      scopeLevel: initialData.scopeLevel || "company",
      scopeId: initialData.scopeId || "",
      primary: Boolean(initialData.primary),
    },
  });

  const scopeLevel = watch("scopeLevel");
  const role = watch("role");

  const userOptions = useMemo(
    () =>
      (options?.users || []).map((user) => ({
        value: user._id,
        label: user.email ? `${user.name} — ${user.email}` : user.name,
      })),
    [options],
  );

  const levelOptions = useMemo(
    () =>
      (options?.scopeLevels || []).map((level) => ({
        value: level,
        label: level.charAt(0).toUpperCase() + level.slice(1),
      })),
    [options],
  );

  const roleOptions = useMemo(
    () =>
      (options?.roles || []).map((value) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
      })),
    [options],
  );

  const scopeOptions = useMemo(
    () => toOptions(options?.scopes?.[scopeLevel]),
    [options, scopeLevel],
  );

  const submit = (values) =>
    onSubmit({
      ...values,
      primary: values.role === "owner" ? values.primary : false,
    });

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5">
      <Controller
        name="userId"
        control={control}
        rules={{ required: "Choose who holds this grant" }}
        render={({ field }) => (
          <Field label="Person" error={errors.userId?.message}>
            <SearchSelect
              options={userOptions}
              value={
                userOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => field.onChange(option?.value || "")}
              placeholder="Select person..."
              disabled={isSubmitting}
            />
          </Field>
        )}
      />

      <Controller
        name="role"
        control={control}
        rules={{ required: "Choose a role" }}
        render={({ field }) => (
          <Field
            label="Role"
            error={errors.role?.message}
            hint={ROLE_HINT[field.value]}
          >
            <SearchSelect
              options={roleOptions}
              value={
                roleOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => {
                field.onChange(option?.value || "");
                if (option?.value !== "owner") setValue("primary", false);
              }}
              placeholder="Select role..."
              searchable={false}
              disabled={isSubmitting}
            />
          </Field>
        )}
      />

      <Controller
        name="scopeLevel"
        control={control}
        rules={{ required: "Choose a level" }}
        render={({ field }) => (
          <Field
            label="Level"
            error={errors.scopeLevel?.message}
            hint={LEVEL_HINT[field.value]}
          >
            <SearchSelect
              options={levelOptions}
              value={
                levelOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => {
                field.onChange(option?.value || "");
                setValue("scopeId", "");
              }}
              placeholder="Select level..."
              searchable={false}
              disabled={isSubmitting}
            />
          </Field>
        )}
      />

      <Controller
        name="scopeId"
        control={control}
        rules={{ required: `Choose which ${scopeLevel}` }}
        render={({ field }) => (
          <Field
            label="Scope"
            error={errors.scopeId?.message}
            hint={
              scopeOptions.length
                ? undefined
                : `No ${scopeLevel}s exist yet — create one first.`
            }
          >
            <SearchSelect
              options={scopeOptions}
              value={
                scopeOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => field.onChange(option?.value || "")}
              placeholder={`Select ${scopeLevel}...`}
              disabled={isSubmitting || !scopeOptions.length}
            />
          </Field>
        )}
      />

      {role === "owner" ? (
        <Controller
          name="primary"
          control={control}
          render={({ field }) => (
            <Field
              label="Primary owner"
              hint="The invoiced contact. A scope can have many owners but only one primary — setting this moves it off whoever holds it now."
            >
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(field.value)}
                  onChange={(event) => field.onChange(event.target.checked)}
                  disabled={isSubmitting}
                />
                Make this the primary owner
              </label>
            </Field>
          )}
        />
      ) : null}

      <div className="flex items-center justify-end gap-3 border-t border-mediumgray pt-4">
        <Button
          type="submit"
          className="black-button"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isExisting ? "Update Grant" : "Create Grant"}
        </Button>
      </div>
    </form>
  );
}
